import { ModelType } from '../../common/enums';
import { CommercialProductSubType } from '../../generated/api/commercialProductSubType';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory';
import type { OfferItemAddon } from '../../common/types/shoppingBasket';
import type { AddOn } from '../../generated/api/addOn';
import type { CommercialProduct } from '../../generated/api/commercialProduct';
import type { OnlineModel } from '../../generated/api/onlineModel';

/**
 * Returns addons that are truly bundled with the offer (isOfferAddOn=true AND addOnPurpose !== 'OPTIONAL').
 * OPTIONAL addons (e.g. Mobiiliturva, Kuka Soittaa, SMS 5000) are user-selectable services,
 * not part of the base offer bundle, so they should NOT be treated as bundled.
 * This is the single source of truth for the bundled/OPTIONAL distinction — used by:
 * - basket display (getDisplayableAddons filters out bundled addons)
 * - basket pricing fallback (sums bundled addon prices when CP has no pricing)
 * - addToShoppingBasket (force-merges bundled addon codes)
 */
export const getBundledOfferAddons = (commercialProduct?: CommercialProduct): AddOn[] => {
  const offerAddons = commercialProduct?.addOnAssociations?.filter(
    addon => addon.isOfferAddOn && addon.addOnPurpose !== 'OPTIONAL'
  );
  const mappedAddOns = commercialProduct?.associatedAddOns?.filter(addon =>
    offerAddons?.some(offerAddon => addon.addOnCode === offerAddon.addOnCode)
  );
  return mappedAddOns || [];
};

/**
 * Checks if an addon is a bundled offer addon (isOfferAddOn=true AND addOnPurpose !== 'OPTIONAL').
 * OPTIONAL addons are user-selectable and should NOT be treated as bundled.
 */
export const isBundledOfferAddon = (addOnCode: string, commercialProduct?: CommercialProduct): boolean => {
  const bundledAddons = getBundledOfferAddons(commercialProduct);
  return bundledAddons.some(addon => addon.addOnCode === addOnCode);
};

/**
 * Returns addon codes for ALL isOfferAddOn=true addons (including OPTIONAL).
 * Salesforce requires all offer addons to be present in the order payload —
 * regardless of addOnPurpose — otherwise downstream validation fails.
 * Use this for basket building and order payload construction.
 * For display purposes (hiding bundled addons), use getBundledOfferAddons/isBundledOfferAddon instead.
 */
export const getAllOfferAddonCodes = (commercialProduct?: CommercialProduct): string[] => {
  return commercialProduct?.addOnAssociations?.filter(addon => addon.isOfferAddOn).map(addon => addon.addOnCode) ?? [];
};

/** Detect M2M products - checks productSubType field or GUID fallback for auth/non-auth models */
export const isMobileM2MProduct = (onlineModel: OnlineModel): boolean => {
  if (onlineModel.category !== OnlineModelCategory.SELF_SERVICE) {
    return false;
  }

  const hasM2MProductSubType =
    onlineModel.commercialProducts?.some(cp => cp.productSubType === CommercialProductSubType.M2M) === true;

  if (hasM2MProductSubType) {
    return true;
  }

  return onlineModel.onlineModelCode === ModelType.MobileM2M;
};

/** Get M2M product variant name from mandatory addon */
export const getM2MProductName = (onlineModel: OnlineModel, addonCode: string, fallbackName: string): string => {
  const mandatoryAddon = onlineModel.addOns?.find(addon => addon.addOnCode === addonCode);
  if (mandatoryAddon?.addOnProductName) {
    return mandatoryAddon.addOnProductName;
  }
  return fallbackName;
};

/**
 * Creates an array of OfferItemAddon objects for each product unit in a basket item.
 * @param quantity - Number of products (determines array length)
 * @param addons - Add-on codes to apply to each product
 * @param addOnAttributes - Optional attributes per add-on code (e.g. voicemail language)
 * @returns Array where each index corresponds to a product's add-ons
 */
export const createOfferItemAddons = (
  quantity: number,
  addons?: string[],
  addOnAttributes?: { [addOnCode: string]: { [key: string]: string } }
): OfferItemAddon[] => {
  return Array.from({ length: quantity }, () => ({
    addOnCodes: addons || [],
    ...(addOnAttributes && Object.keys(addOnAttributes).length > 0 ? { addOnAttributes } : {}),
  }));
};
